import * as React from 'react';
import { findDOMNode } from 'react-dom';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import _ from 'lodash';
import { Form, Row, Col, Card } from '@beehive/components';
import { element, disabledDate } from 'utils';
import { renderForm, HandleChagneParams } from '../Edit/BasicInfo';
import { SUPPLIER_CONTRACTS_KEYS_MAPPING } from '../utils';
import PropTypes from 'prop-types';
import messages from 'messages/CompanyManagement';

const CONTRACT_ITEM_KEYS = ['code', 'upper_limit'];
const CONTRACT_CITYS = [['gz', 'hz'], ['sh', 'hk']];
const BASE_INFO_KEYS = ['currency', 'bank_info.invoice_type', 'contract_begin_at', 'contract_end_at'];

const ContractItem: React.SFC = (props: any, context: any) => {
  const { intl } = context;
  const { translated_city, prefixCls, options, handleChange } = props;
  const classes = classNames(`${prefixCls}-item`);
  return (
    <div className={classes}>
      <div className={`${prefixCls}-item-title`}>{translated_city}{intl.formatMessage(messages.contract)}</div>
      <Form
        isLabelVertical
        mode="edit"
        itemColProps={{ span: 24 }}
        itemLabelColProps={{ span: 4 }}
        itemInputColProps={{ span: 20 }}
      >
        {
          CONTRACT_ITEM_KEYS.map((key: any) => {
            const value = props[key];
            const editProps = SUPPLIER_CONTRACTS_KEYS_MAPPING[key].editProps;
            return (
              <Form.Item
                itemKey={key}
                label={SUPPLIER_CONTRACTS_KEYS_MAPPING[key].name}
                key={key}
                {...editProps}
              >
                {renderForm({ key, value, options, data: SUPPLIER_CONTRACTS_KEYS_MAPPING, handleChange })}
              </Form.Item>
            );
          })
        }
      </Form>
    </div>
  );
};

ContractItem.contextTypes = {
  intl: PropTypes.object,
};

export default class SupplierContracts extends React.Component<any, any> {

  public static contextTypes = {
    intl: PropTypes.object,
  };

  private resizeObserver: ResizeObserver | undefined;
  private thisDOM: Element | undefined;
  private contractsRef: React.RefObject<HTMLDivElement>;
  private formRef: React.RefObject<typeof Form>;

  constructor(props: any) {
    super(props);
    this.contractsRef = React.createRef();
    this.formRef = React.createRef();
  }

  public componentDidMount() {
    this.resizeObserver = new ResizeObserver(this.handleResize);
    const thisDOM = findDOMNode(this);
    if (thisDOM instanceof Element) {
      this.thisDOM = thisDOM;
      this.resizeObserver.observe(thisDOM);
    }
  }

  public shouldComponentUpdate(nextProps: any, nextState: any) {
    if (this.props.showError !== nextProps.showError) {
      return true;
    }
    const pickKeys = [...BASE_INFO_KEYS, 'detailError.messages', 'supplier_contracts', 'loading', 'loaded'];
    if (_.isEqual(_.pick(this.props.data, pickKeys), _.pick(nextProps.data, pickKeys))) {
      return false;
    }
    return true;
  }

  public componentWillUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private handleResize = (entries: ResizeObserverEntry[], observer: ResizeObserver): void => {
    if (this.thisDOM) {
      // @ts-ignore
      const parentRow = element.closest(this.thisDOM, '.beehive-row');
      if (parentRow) {
        const rowHeight = parentRow.clientHeight;
        const thisHeight = this.thisDOM.clientHeight;
        if (thisHeight && rowHeight && thisHeight !== rowHeight && this.contractsRef.current) {
          const extraHeight = thisHeight - this.contractsRef.current.clientHeight;
          const itemHeight = (Math.abs(rowHeight - extraHeight) - 24) / 2;
          this.thisDOM.querySelectorAll('.contracts-info-item').forEach((el: any) => {
            el.style.height = `${itemHeight}px`;
          });
        }
      }
    }
  }

  // 修改内容
  private handleChange = (params: HandleChagneParams) => {
    const { onChange, data } = this.props;
    let newData = _.cloneDeep(data);
    const { key, value } = params;
    _.set(newData, key, value);
    // 注意`service_info.total_employee`是一个整体，直接get会拿不到值
    const error = _.get(_.get(newData, `detailError.messages`), key);
    const newDetailError = newData.detailError;
    if (error) {
      delete newDetailError.messages[key];
    }
    newData = {
      ...newData,
      detailError: { ...newDetailError },
    };
    onChange(newData);
  }

  private handleChangeContractItem = (params: any) => {
    const { onChange, data } = this.props;
    const { city, key, value } = params;
    const supplierContracts = _.cloneDeep(_.get(data, 'supplier_contracts', []));
    const item = supplierContracts.find((item: any) => item.city === city);
    if (item) {
      _.set(item, key, value);
    } else {
      supplierContracts.push({ [key]: value, city });
    }

    onChange({
      ...data,
      supplier_contracts: supplierContracts,
    });
  }

  private getFormItemProps = () => {
    const { data } = this.props;
    return {
      contract_begin_at: {
        disabledDate: (val: any) => {
          return disabledDate(_.get(data, 'contract_end_at'), val);
        },
      },
      contract_end_at: {
        disabledDate: (val: any) => {
          return disabledDate(val, _.get(data, 'contract_begin_at'));
        },
      },
    };
  }

  public isFormValid = (): boolean => {
    if (this.formRef.current) {
      return this.formRef.current.valid();
    }
    return true;
  }

  public render() {
    const { intl } = this.context;
    const { data, prefixCls = 'contracts-info', className, showError, saveClicked } = this.props;
    const {
      supplier_contracts = [],
      loading,
      options,
    } = data;

    const errorMsg = _.pick(_.get(data, 'detailError.messages'), BASE_INFO_KEYS);
    const classes = classNames(prefixCls, className);
    const formItemProps = this.getFormItemProps();
    return (
      <Card
        titleNode={intl.formatMessage(messages.contractInfo)}
        className={classes}
        loading={loading}
      >
        <Form
          isLabelVertical
          itemColProps={{ span: 6 }}
          errorMsg={errorMsg}
          showError={showError || saveClicked}
          className={`${prefixCls}-base-info`}
          mode="edit"
          ref={this.formRef}
          showClientError
        >
          {
            BASE_INFO_KEYS.map((key: any) => {
              const value = _.get(data, key);
              const editProps = SUPPLIER_CONTRACTS_KEYS_MAPPING[key].editProps;
              return (
                <Form.Item
                  itemKey={key}
                  label={SUPPLIER_CONTRACTS_KEYS_MAPPING[key].name}
                  key={key}
                  {...editProps}
                >
                  {
                    renderForm({
                      key,
                      value,
                      options,
                      data: SUPPLIER_CONTRACTS_KEYS_MAPPING,
                      handleChange: this.handleChange,
                      otherProps: formItemProps[key],
                    })
                  }
                </Form.Item>
              );
            })
          }
        </Form>
        <div ref={this.contractsRef}>
          {
            CONTRACT_CITYS.map((row: any, idx: number) => {
              return (
                <Row gutter={24} key={idx} className={`${prefixCls}-item-row`}>
                  {
                    row.map((key: any) => {
                      const cityProps = supplier_contracts.find((i: any) => i.city === key) || {};
                      return (
                        <Col key={key} span={12}>
                          <ContractItem
                            {...cityProps}
                            city={key}
                            translated_city={SUPPLIER_CONTRACTS_KEYS_MAPPING[key].name}
                            key={key}
                            prefixCls={prefixCls}
                            options={options}
                            handleChange={(params: HandleChagneParams) => {
                              this.handleChangeContractItem({ ...params, city: key });
                            }}
                          />
                        </Col>
                      );
                    })
                  }
                </Row>
              );
            })
          }
        </div>
      </Card>
    );
  }
}
